import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

/**
 * Componente Button con diseño moderno y efectos visuales
 * Soporta iconos alineados con el texto
 */
export const Button: React.FC<ButtonProps> = ({ 
  variant = 'primary', 
  size = 'md', 
  className = '', 
  children, 
  icon,
  iconPosition = 'left',
  ...props 
}) => {
  const baseClasses = `
    font-semibold rounded-xl transition-all duration-300 ease-in-out
    focus:outline-none focus:ring-4 focus:ring-emerald-500/30
    disabled:opacity-50 disabled:cursor-not-allowed
    transform hover:scale-[1.02] active:scale-[0.98]
    relative overflow-hidden
    flex items-center justify-center gap-2
  `;
  
  const variants = {
    primary: `
      bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg 
      hover:from-emerald-600 hover:to-emerald-700 hover:shadow-emerald-500/25 hover:shadow-xl
    `,
    secondary: `
      bg-slate-800 hover:bg-slate-700 text-white border-2 border-slate-600
      hover:border-emerald-500/50 shadow-lg
    `,
    outline: `
      border-2 border-emerald-500 text-emerald-500 hover:bg-emerald-500 hover:text-white
      hover:shadow-emerald-500/25 hover:shadow-xl
    `
  };
  
  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg'
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-4 h-4', 
    lg: 'w-5 h-5'
  };

  const renderContent = () => {
    if (!icon) {
      return children;
    }

    // Simplificar la lógica del icono para evitar errores de TypeScript
    let iconElement: React.ReactNode;
    
    if (React.isValidElement(icon)) {
      // Si es un elemento React válido, clonarlo con las clases apropiadas
      const existingClassName = (icon.props as any)?.className || '';
      iconElement = React.cloneElement(icon as React.ReactElement<any>, {
        className: `${iconSizes[size]} ${existingClassName}`.trim()
      });
    } else {
      // Si no es un elemento React, envolverlo en un span
      iconElement = <span className={iconSizes[size]}>{icon}</span>;
    }

    if (iconPosition === 'right') {
      return (
        <>
          {children}
          {iconElement}
        </>
      );
    }

    return (
      <>
        {iconElement}
        {children}
      </>
    );
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      <span className="relative z-10 flex items-center gap-2">
        {renderContent()}
      </span>
    </button>
  );
};